import { useMemo } from 'react';
import { IFormElementOptionProp } from 'models/form';
import { EDictionaryScope, useDictionaryScope } from 'models/dictionaryScopeContext';

const toSelectedKeys = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined).map(String);
  }
  if (value === null || value === undefined || value === '') {
    return [];
  }
  return [String(value)];
};

export interface IArchivedOptions {
  visibleOptions: IFormElementOptionProp[];
  archivedSelectedOptions: IFormElementOptionProp[];
  hasArchivedSelection: boolean;
  isArchivedValue: (value: unknown) => boolean;
}

export const useArchivedOptions = (
  options: IFormElementOptionProp[],
  selectedValue: unknown,
  scopeOverride?: EDictionaryScope,
): IArchivedOptions => {
  const contextScope = useDictionaryScope();
  const scope = scopeOverride ?? contextScope;
  const selectedKeys = toSelectedKeys(selectedValue);
  const selectedKey = selectedKeys.join('|');

  return useMemo(() => {
    if (scope === EDictionaryScope.search) {
      return {
        visibleOptions: options,
        archivedSelectedOptions: [],
        hasArchivedSelection: false,
        isArchivedValue: () => false,
      };
    }

    const isSelected = (option: IFormElementOptionProp) =>
      selectedKeys.includes(String(option.value));

    const visibleOptions = options.filter(
      (option) => !option.isArchived || isSelected(option),
    );
    const archivedSelectedOptions = visibleOptions.filter(
      (option) => option.isArchived && isSelected(option),
    );

    return {
      visibleOptions,
      archivedSelectedOptions,
      hasArchivedSelection: archivedSelectedOptions.length > 0,
      isArchivedValue: (value: unknown) =>
        archivedSelectedOptions.some((option) => String(option.value) === String(value)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selectedKey, scope]);
};
